import React from "react"
import type {
  EstablishmentData, MunicipalityLookup, ResidueGroup, TechnicianData, WizardArchetype
} from "../../../types"
import { GROUP_META } from "../constants"
import { StepNav } from "../ui"

interface Props {
  archetype: WizardArchetype
  establishment: EstablishmentData
  technician: TechnicianData
  groups: ResidueGroup[]
  municipality: MunicipalityLookup | null
  submitting: boolean
  errors: string[]
  onBack: () => void
  onSubmit: () => void
}

export default function ReviewStep({
  archetype, establishment, technician, groups, municipality, submitting, errors, onBack, onSubmit
}: Props) {
  return (
    <section>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink">Confira e gere o preview</h1>
      <p className="mt-3 text-gray-600">
        Seu plano cobre <span className="font-semibold text-ink">{groups.length} grupos de resíduo</span> —
        revise os dados antes de gerar o preview gratuito.
      </p>

      <dl className="mt-8 space-y-4">
        <ReviewCard title="Estabelecimento">
          <p className="font-semibold text-ink">{establishment.fantasy_name}</p>
          <p className="text-sm text-gray-600">
            {archetype.label} · {establishment.municipio}/{establishment.uf}
            {establishment.cnpj && <> · CNPJ {establishment.cnpj}</>}
          </p>
        </ReviewCard>

        <ReviewCard title="Responsável Técnico">
          <p className="font-semibold text-ink">{technician.name}</p>
          <p className="text-sm text-gray-600">{technician.council}/{technician.uf} {technician.council_number}</p>
        </ReviewCard>

        <ReviewCard title="Grupos de resíduo (RDC 222)">
          <div className="flex flex-wrap gap-2">
            {groups.map(group => (
              <span key={group} className={`rounded-lg border px-3 py-1 text-sm font-semibold ${GROUP_META[group].stamp}`}>
                {GROUP_META[group].name}
              </span>
            ))}
          </div>
        </ReviewCard>
      </dl>

      {municipality?.requires_engineer_art && (
        <div className="mt-6 rounded-xl border-2 border-accent-500/40 bg-accent-400/10 px-4 py-3 text-sm text-ink">
          <p className="font-semibold">Atenção: seu município pode exigir ART de engenheiro</p>
          <p className="mt-1">
            Em {establishment.municipio}/{establishment.uf}, a vigilância costuma exigir Anotação de
            Responsabilidade Técnica de engenheiro ambiental na assinatura do PGRSS. Você ainda pode
            gerar o documento — mas confirme essa exigência antes de protocolar.
          </p>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="font-semibold">Corrija antes de continuar:</p>
          <ul className="mt-1 list-disc pl-5">
            {errors.map(error => <li key={error}>{error}</li>)}
          </ul>
        </div>
      )}

      <p className="mt-6 text-xs text-gray-500">
        Ao continuar, seus dados são usados apenas pra montar o documento (LGPD). O preview é gratuito —
        você só paga se quiser baixar o plano completo.
      </p>

      <StepNav
        onBack={onBack}
        onContinue={onSubmit}
        continueLabel={submitting ? "Gerando preview…" : "Ver meu preview grátis"}
        continueDisabled={submitting}
      />
    </section>
  )
}

function ReviewCard({ title, children }: { title: string, children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border-2 border-brand-100 bg-white p-5">
      <dt className="text-xs font-semibold uppercase tracking-wide text-brand-700">{title}</dt>
      <dd className="mt-2">{children}</dd>
    </div>
  )
}
